import type { ClinicSpeechTerm } from '../types';

/**
 * 無音・極短録音で Whisper が返しがちなフレーズ（YouTube 風の締め等）。
 * そのまま挿入すると誤解を招くため、**全文がこれに一致するときだけ**捨てる。
 */
export function isWhisperSilenceHallucination(raw: string): boolean {
  const trimmed = raw.trim();
  if (!trimmed) return true;

  const collapsed = trimmed
    .normalize('NFKC')
    .replace(/\s+/g, '')
    .replace(/[。．，、！!？?♪♫\-_]/g, '')
    .toLowerCase();

  if (!collapsed) return true;

  const phrases = [
    'ご視聴ありがとうございました',
    'ご視聴ありがとうございます',
    'ご清聴ありがとうございました',
    'ご清聴ありがとうございます',
    'ありがとうございました',
    'ありがとうございます',
    'thankyouforwatching',
    'thanksforwatching',
  ];

  if (phrases.some((p) => collapsed === p.normalize('NFKC').replace(/\s+/g, '').toLowerCase())) {
    return true;
  }

  // 字幕のみ・極短のノイズ文
  if (/^字幕/.test(trimmed) && trimmed.length <= 40) return true;

  return false;
}

/**
 * 文の本体が落ち、否定の語尾だけ返った疑い（先頭が無音扱いで切れた等）。
 * このまま挿入すると誤記になるため、挿入せずユーザーに再録音を促す。
 */
export function isWhisperTruncatedNegativeTailOnly(raw: string): boolean {
  const t = raw.trim().replace(/[。．，、！!？?♪♫\s]/g, '');
  return t === 'はなく' || t === 'はない';
}

/**
 * Whisper / 旧ブラウザ音声の起こし結果を申し送り向けに整形する。
 * - 入力の NFKC 正規化（互換・全半角）
 * - 句読点の言い換え
 * - 歯科略語の置換
 * - 医院辞書（よみ→語句）
 * - 英字略語の小文字→大文字（ecg→ECG）
 */
export function normalizeSpeechTranscript(
  input: string,
  clinicSpeechTerms: ClinicSpeechTerm[]
): string {
  // 互換文字・全半角のゆれを先にそろえる（Whisper 出力でマッチし損ねるのを防ぐ）
  let t = input
    .normalize('NFKC')
    .replace(/まる|句点/g, '。')
    .replace(/てん|読点/g, '、')
    .replace(/改行/g, '\n');

  t = t
    .replace(/こんちちゅう|コンチチュウ|こんち中|コンチ中|根治ちゅう/g, '根治中')
    .replace(/こんち|コンチ/g, '根治')
    .replace(/根治/g, '根治')
    .replace(/こんかんちりょう|コンカンチリョウ|根管治療/g, '根管治療')
    .replace(/こん\s*かん|コン\s*カン|こんかん|コンカン|根幹|婚姦|混感/g, '根管')
    .replace(/こんじゅう|コンジュウ|根充/g, '根管充填')
    .replace(/こんかんじゅうてん|コンカンジュウテン|根管充填/g, '根管充填')
    .replace(/根管重点/g, '根管充填')
    .replace(/仮封\s*剤/g, '仮封材')
    .replace(/ルートプレーニング/g, 'ルートプレーニング')
    .replace(/えんど|エンド/g, '根管治療')
    .replace(/ぺりお|ペリオ/g, '歯周病')
    // 「しにく」の同音誤変換（死≠歯）。退縮と同音の「退宿」、ひらがな・重複しにくにも対応
    .replace(/死にく\s*退[縮宿]|死肉\s*退[縮宿]/g, '歯肉退縮')
    .replace(/歯肉\s*退宿/g, '歯肉退縮')
    .replace(/しにくしにく\s*たいしゅく/g, '歯肉退縮')
    .replace(/しにく\s*たいしゅく/g, '歯肉退縮')
    .replace(/シニクシニク\s*タイシュク/g, '歯肉退縮')
    .replace(/シニク\s*タイシュク/g, '歯肉退縮')
    // 「ししゅう」の同音誤変換
    .replace(/刺繍/g, '歯周')
    // 咬合（こうごう）が同音の「交互」に化ける。交互に＝交替の意味は触らない
    .replace(/交互(?=平面|調整|高径|接触|関係)/g, '咬合')
    .replace(/交互(\s*)(?=[がはをの、。．!！?？]|$)/g, '咬合$1')
    .replace(/こうごうへいめん|こうごう\s*へいめん/g, '咬合平面')
    .replace(/こうごうちょうせい|こうごう\s*ちょうせい/g, '咬合調整')
    .replace(/コウゴウヘイメン|コウゴウ\s*ヘイメン/g, '咬合平面')
    .replace(/コウゴウチョウセイ|コウゴウ\s*チョウセイ/g, '咬合調整')
    .replace(/こうごう(\s*)(?=[がはをの、。．!！?？]|$)/g, '咬合$1')
    .replace(/コウゴウ(\s*)(?=[がはをの、。．!！?？]|$)/g, '咬合$1')
    .replace(/こうちょう|コウチョウ|咬調/g, '咬合調整')
    // 既往歴（標準はきおうれき／発音できょう・きようと聞こえるとひらがなで出る）
    .replace(/起用歴|起応歴|機往歴/g, '既往歴')
    .replace(/清暦/g, '既往歴')
    .replace(/きおうれき|きょうれき|きようれき/g, '既往歴')
    .replace(/キオウレキ|キョウレキ|キヨウレキ/g, '既往歴')
    // 麻酔時（ますいじ）— Whisper が「マス維持」等に化けやすい
    .replace(/マス\s*維持|ます\s*維持/g, '麻酔時')
    .replace(/マスイジ|マス\s*イジ/g, '麻酔時')
    // 臼歯の同音誤変換（だいきゅうし → 第9指 等）
    .replace(/第[0-9９]指/g, '大臼歯')
    .replace(/だいきゅうし|ダイキュウシ/g, '大臼歯')
    .replace(/しょうきゅうし|ショウキュウシ/g, '小臼歯')
    // 虫歯・進行の同音誤変換（蒸し歯・振興／進行と振興はどちらもしんこう）
    .replace(/蒸し歯の振興はなく/g, '虫歯の進行はなく')
    .replace(/虫歯の振興はなく/g, '虫歯の進行はなく')
    .replace(/蒸し歯の振興/g, '虫歯、進行')
    .replace(/虫歯の振興/g, '虫歯の進行')
    .replace(/蒸し歯/g, '虫歯');

  // Web Speech で「こんち」が地名の漢字に化けるケース（申し送り文脈では根治の略）
  t = t.replace(/今治/g, '根治');

  const clinicPairs = clinicSpeechTerms
    .map((row) => ({
      reading: row.reading.trim(),
      phrase: row.phrase.trim(),
    }))
    .filter((x) => x.reading.length > 0 && x.phrase.length > 0 && x.reading !== x.phrase)
    .sort((a, b) => b.reading.length - a.reading.length);

  for (const { reading, phrase } of clinicPairs) {
    const esc = reading.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    t = t.replace(new RegExp(esc, 'g'), phrase);
  }

  // 2〜10 文字の英小文字トークンを大文字化（pmtc→PMTC 等）
  t = t.replace(/\b([a-z]{2,10})\b/g, (_, w: string) => w.toUpperCase());

  return t;
}
